import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync, faCheck } from "@fortawesome/free-solid-svg-icons"
import {
	corpsDelta,
	fetchDispatchAnnulees,
	putDepannageDispatch,
	putDepannageDispatchReset,
} from "../../../store/actions/depannageDispatchActions"
import { DEP_STATE_READY_TO_DISPATCH } from "../../../utilities/Addons"
import { COLORS } from "../../../utilities/Constants"
import CustomModal from "../../basics/interface/CustomModal"
import Loader from "../../basics/Loader"
import { DISPATCH_POLL_MS } from "./DepDispatchATraiter"
import DepMissionCardV2 from "./DepMissionCardV2"

// Onglet « Annulées » : missions à l'état 80 (annulées par le bureau ou par le
// dépanneur). Seule action possible : « Remettre au dispatch » (→ prêt au
// dispatch, état 20) — la mission a été nettoyée à l'annulation (plus de
// dépanneur/planification), elle repart propre dans la pile À traiter.
// La purge des annulées (auto hebdo ? manuelle ?) reste à décider — cf.
// tasks-depannage-suite.md.
const DepDispatchAnnulees = () => {
	const dispatch = useDispatch()
	// Mission en cours de réactivation (confirmation modal, convention V2).
	const [aReactiver, setAReactiver] = useState(null)
	const { depsAnnulees, fetchDepsAnnulees, putDepsToDispatch } = useSelector(
		(state) => state.depannageDispatchInfo,
	)
	const busy = putDepsToDispatch.saving || putDepsToDispatch.saved
	// Revue 5 (🟡) : le slice est partagé — l'erreur d'une tentative
	// précédente ne réapparaît ni à l'ouverture ni après la fermeture.
	const ouvrir = (dm) => {
		dispatch(putDepannageDispatchReset())
		setAReactiver(dm)
	}
	const fermer = () => {
		dispatch(putDepannageDispatchReset())
		setAReactiver(null)
	}

	useEffect(() => {
		// Purge tout état saved/error laissé par un autre onglet (slice partagé).
		dispatch(putDepannageDispatchReset())
		dispatch(fetchDispatchAnnulees())
		const interval = setInterval(
			() => dispatch(fetchDispatchAnnulees()),
			DISPATCH_POLL_MS,
		)
		return () => clearInterval(interval)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const reactiver = () => {
		if (busy || !aReactiver) return
		// GE-62 (revue phase B, 🟠2) : delta + version lue — une liste
		// périmée (mission déjà réactivée et redispatchée ailleurs) prend un
		// 409 au lieu de la faire redescendre à 20.
		dispatch(
			putDepannageDispatch(
				corpsDelta(aReactiver, { etat: DEP_STATE_READY_TO_DISPATCH }),
			),
		).then(() => dispatch(fetchDispatchAnnulees()))
	}

	// ✓ 1 s puis fermeture du modal + refresh de la liste.
	useEffect(() => {
		if (!putDepsToDispatch.saved) return
		const timeout = setTimeout(() => {
			dispatch(putDepannageDispatchReset())
			setAReactiver(null)
			dispatch(fetchDispatchAnnulees())
		}, 1000)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [putDepsToDispatch.saved])

	return (
		<>
			{aReactiver && (
				<CustomModal
					title="Remettre au dispatch ?"
					subtitle={`${aReactiver.marque} ${aReactiver.modele} ${
						aReactiver.plaques ? `— ${aReactiver.plaques}` : ""
					}`.trim()}
					onClose={busy ? () => {} : fermer}
					showCloseButton={!busy}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: fermer,
							disabled: busy,
						},
						{
							title: putDepsToDispatch.saving ? (
								<FontAwesomeIcon icon={faSync} spin />
							) : putDepsToDispatch.saved ? (
								<FontAwesomeIcon icon={faCheck} />
							) : (
								"Remettre au dispatch"
							),
							styleName: "primary",
							onClick: reactiver,
							disabled: busy,
						},
					]}
				>
					<div style={{ fontSize: 13, color: COLORS.text }}>
						La mission repartira dans « À traiter » (prêt au
						dispatch), sans dépanneur ni planification.
					</div>
					{putDepsToDispatch.error && (
						<div style={st.error}>
							{putDepsToDispatch.error.httpStatus === 409
								? `${putDepsToDispatch.error.message} La liste a été relue : fermez et réessayez.`
								: "La remise au dispatch a échoué. Réessayez."}
						</div>
					)}
				</CustomModal>
			)}

			{fetchDepsAnnulees.loading && depsAnnulees.length === 0 ? (
				<Loader variant="ring" message="Chargement…" />
			) : depsAnnulees.length === 0 ? (
				<div style={st.empty}>Aucune mission annulée</div>
			) : (
				<div style={st.grid}>
					{depsAnnulees.map((dm) => (
						<DepMissionCardV2
							key={`annulee_${dm.id}`}
							dep={dm}
							action={{
								label: "Remettre au dispatch",
								onClick: () => ouvrir(dm),
								secondaire: true,
							}}
						/>
					))}
				</div>
			)}
		</>
	)
}

const st = {
	grid: { display: "flex", flexWrap: "wrap", gap: 12 },
	empty: { padding: "24px 0", fontSize: 13, color: COLORS.textLight },
	error: { fontSize: 13, color: COLORS.danger, marginTop: 10 },
}

export default DepDispatchAnnulees
